
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Employee, EmployeeStatus } from '../types';
import { getProbationPeriodFromHiring } from '../probationPeriod';
import { countEmployeeAbsencesInMonth } from '../utils/absences';
import { canonicalizeEmployeeRole } from '../utils/textFormat';

interface CardProps {
  employee: Employee;
  roleColor?: string;
  onEdit?: (employee: Employee) => void;
  onDelete?: (id: string) => void;
  /** Quando false, desabilita arrastar o card entre colunas. */
  canDrag?: boolean;
  /** Data de referência para indicador de faltas do mês corrente. */
  referenceDate?: Date;
}

const getInitials = (name: string) => {
  if (!name || name.trim().length === 0) return '?';
  const parts = name.trim().split(/\s+/);
  return parts[0][0].toUpperCase();
};

const CardComponent: React.FC<CardProps> = ({
  employee,
  roleColor,
  onEdit,
  onDelete,
  canDrag = true,
  referenceDate = new Date(),
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: employee.id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCompleted = employee.status === EmployeeStatus.Completed;
  const absencesThisMonth = countEmployeeAbsencesInMonth(employee, referenceDate);
  const hasAbsences = absencesThisMonth > 0;
  const probation = getProbationPeriodFromHiring(employee.hiringDate);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(employee);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(employee.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(canDrag ? listeners : {})}
      className={`group relative p-2 rounded-lg bg-white shadow-sm border border-slate-100 hover:border-secondary-accent/30 transition-all hover:shadow-md hover:-translate-y-0.5 ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
    >
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all z-10">
        {onEdit && (
        <button 
            onClick={handleEditClick}
            className="size-5 flex items-center justify-center rounded-md bg-slate-50 hover:bg-secondary-accent hover:text-white text-slate-400 transition-colors shadow-sm"
            onPointerDown={(e) => e.stopPropagation()}
        >
             <span className="material-symbols-outlined !text-[12px]">edit</span>
        </button>
        )}
        {onDelete && (
        <button 
            onClick={handleDeleteClick}
            className="size-5 flex items-center justify-center rounded-md bg-slate-50 hover:bg-status-red hover:text-white text-slate-400 transition-colors shadow-sm"
            onPointerDown={(e) => e.stopPropagation()}
        >
             <span className="material-symbols-outlined !text-[12px]">delete</span>
        </button>
        )}
      </div>

      <div className="flex justify-between items-start">
        <div className="flex gap-2 items-center overflow-hidden">
          <div
            className={`h-7 w-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold shadow-sm ring-1 ring-slate-100 ${isCompleted ? 'opacity-60 grayscale' : ''}`}
            style={{ backgroundColor: roleColor || '#5C8FF0' }}
          >
            {getInitials(employee.name)}
          </div>
          <div className="flex flex-col justify-center overflow-hidden">
            <h4 className="font-semibold text-[12px] text-slate-800 leading-tight truncate pr-1.5">{employee.name}</h4>
            <p className="text-[10px] text-slate-400 font-medium leading-tight mt-0.5 truncate">{canonicalizeEmployeeRole(employee.role)}</p>
          </div>
        </div>
        <div className="group-hover:opacity-0 transition-opacity flex gap-0.5 shrink-0 ml-0.5">
            {hasAbsences && (
                <div className="flex items-center justify-center size-4 rounded-full border border-status-red/30 text-status-red bg-red-50" title={`Faltas no mês: ${absencesThisMonth}`}>
                    <span className="text-[8px] font-bold">F</span>
                </div>
            )}
            {probation && (
                <div className={`flex items-center justify-center size-4 rounded-full border font-bold text-[8px] ${probation === '45' ? 'border-orange-200 text-orange-500 bg-orange-50' : 'border-status-blue/30 text-secondary-accent bg-blue-50'}`} title={`Experiência: ${probation} dias (por admissão)`}>
                    {probation}
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

// Memoização para evitar re-renders desnecessários durante drag-and-drop
export const Card = React.memo(CardComponent, (prevProps, nextProps) => {
  // Comparação profunda dos dados relevantes
  return (
    prevProps.employee.id === nextProps.employee.id &&
    prevProps.employee.name === nextProps.employee.name &&
    prevProps.employee.role === nextProps.employee.role &&
    prevProps.employee.status === nextProps.employee.status &&
    prevProps.employee.hiringDate === nextProps.employee.hiringDate &&
    countEmployeeAbsencesInMonth(prevProps.employee, prevProps.referenceDate ?? new Date()) ===
      countEmployeeAbsencesInMonth(nextProps.employee, nextProps.referenceDate ?? new Date()) &&
    prevProps.referenceDate?.getTime() === nextProps.referenceDate?.getTime() &&
    prevProps.roleColor === nextProps.roleColor &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.canDrag === nextProps.canDrag
  );
});
